import { type CountryCode, type ProjectCode } from '@constants'
import { useDeepCompareEffect } from '@dni-hooks/use-deep-compare-effect'
import { type DeliveryClass } from '@hooks-api/types/dataModel/shipping'
import { useMemo } from 'react'
import * as couponService from '../../core/couponService'
import { type GiveawayValue, type RegularGiveawayValue } from '../../types'
import { type PlatformType } from './store-map'
import { useFpB2bCouponService } from './use-fp-b2b-coupon-service'

export interface UseFpB2bSyncGiveawayValuesToFormArgs {
  projectCode: ProjectCode
  countryCode: CountryCode
  campaignId: number
  campaignCode: string | undefined
  deliveryClass: DeliveryClass | undefined
  onGiveawayValuesChange?: (giveawayValues?: GiveawayValue[]) => void
  onRegularGiveawayValuesChange?: (regularGiveawayValues?: RegularGiveawayValue[]) => void
  platform: PlatformType
}

export const useFpB2bSyncGiveawayValuesToForm = (args: UseFpB2bSyncGiveawayValuesToFormArgs) => {
  const {
    projectCode,
    countryCode,
    campaignId,
    campaignCode,
    deliveryClass,
    onGiveawayValuesChange,
    onRegularGiveawayValuesChange,
    platform,
  } = args
  const {
    appliedGiveaways,
    selectedCouponGiveawayMap,
    getRegularGiveawaysFilteredByDeliveryClass,
  } = useFpB2bCouponService(campaignId, projectCode, countryCode, campaignCode, platform)
  const filteredRegularGiveaways = useMemo(() => deliveryClass ? getRegularGiveawaysFilteredByDeliveryClass(deliveryClass) : [], [getRegularGiveawaysFilteredByDeliveryClass, deliveryClass])
  const regularGiveawayList = useMemo(() => filteredRegularGiveaways.flatMap(regularGiveaway => regularGiveaway.criterions), [filteredRegularGiveaways])

  useDeepCompareEffect(() => {
    const giveawaysValues: GiveawayValue[] | undefined = appliedGiveaways?.map((giveaway) => {
      const { coupon, ...criterion } = giveaway
      const {
        couponCriterionId,
        accumulatedGiftSelectionCount,
        totalSelected,
        giveawayValuesWithValidateStock,
        isGiveAll,
        criterionCount,
      } = couponService.getGiveawaySelectProps(coupon, criterion, selectedCouponGiveawayMap)
      return {
        couponCriterionId,
        totalSelected,
        accumulatedGiftSelectionCount,
        giveawayValuesWithValidateStock,
        isGiveAll,
        criterionCount,
      }
    })
    onGiveawayValuesChange?.(giveawaysValues)
  }, [appliedGiveaways, onGiveawayValuesChange, selectedCouponGiveawayMap])

  useDeepCompareEffect(() => {
    const regularGiveawaysValues: RegularGiveawayValue[] = regularGiveawayList.map((criterion) => {
      const {
        couponCriterionId,
        isAllUnavailable,
        isApplied,
        value,
        selectedRegularGiveawayStock,
      } = couponService.getRegularGiveawaySelectProps(criterion, selectedCouponGiveawayMap)
      return {
        isApplied,
        isAllUnavailable,
        selectedRegularGiveawayStock,
        couponCriterionId,
        ...value,
      }
    })
    onRegularGiveawayValuesChange?.(regularGiveawaysValues)
  }, [onRegularGiveawayValuesChange, regularGiveawayList, selectedCouponGiveawayMap])
}
